import type { SavedMapView } from "./map-camera.ts";

/** sessionStorage: home map camera to restore after quest edit / route editor navigation. */
export const HOME_BROWSE_RETURN_KEY = "mapus.homeBrowseReturn";

const SCHEMA_VERSION = 1;
const MAX_AGE_MS = 30 * 60 * 1000;

export type HomeBrowseReturn = {
  v: typeof SCHEMA_VERSION;
  questId: string | null;
  view: SavedMapView & { framed: boolean };
  /** When true, route-editor Done and quest bar Exit should land on `/` and restore this view. */
  returnToHome: boolean;
  savedAt: number;
};

function sessionStore(): Storage | null {
  try {
    return globalThis.sessionStorage;
  } catch {
    return null;
  }
}

function isView(raw: unknown): raw is SavedMapView & { framed: boolean } {
  if (!raw || typeof raw !== "object") return false;
  const v = raw as Record<string, unknown>;
  const c = v.center;
  return (
    Array.isArray(c) &&
    c.length === 2 &&
    typeof c[0] === "number" &&
    typeof c[1] === "number" &&
    typeof v.zoom === "number" &&
    typeof v.bearing === "number" &&
    typeof v.pitch === "number" &&
    typeof v.framed === "boolean"
  );
}

export function writeHomeBrowseReturn(
  payload: Omit<HomeBrowseReturn, "v" | "savedAt">,
): void {
  const store = sessionStore();
  if (!store) return;
  const data: HomeBrowseReturn = { v: SCHEMA_VERSION, ...payload, savedAt: Date.now() };
  try {
    store.setItem(HOME_BROWSE_RETURN_KEY, JSON.stringify(data));
  } catch {
    /* quota / private mode */
  }
}

export function readHomeBrowseReturn(now = Date.now()): HomeBrowseReturn | null {
  const store = sessionStore();
  if (!store) return null;
  try {
    const raw = store.getItem(HOME_BROWSE_RETURN_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return null;
    const row = parsed as Record<string, unknown>;
    if (row.v !== SCHEMA_VERSION) return null;
    if (typeof row.savedAt !== "number" || now - row.savedAt > MAX_AGE_MS) return null;
    if (typeof row.returnToHome !== "boolean") return null;
    if (row.questId != null && typeof row.questId !== "string") return null;
    if (!isView(row.view)) return null;
    return row as HomeBrowseReturn;
  } catch {
    return null;
  }
}

export function clearHomeBrowseReturn(): void {
  const store = sessionStore();
  if (!store) return;
  try {
    store.removeItem(HOME_BROWSE_RETURN_KEY);
  } catch {
    /* ignore */
  }
}
